import React, { forwardRef, useCallback, useState } from "react";
import { JsonLd } from "react-schemaorg";
import type { ImageObject } from "schema-dts";

import type { ImageProps } from "./styles";
import { StyledImage } from "./styles";

export type Props = {
  className?: string;
  disableLinkingData?: boolean;
  lazyload?: boolean;
} & Omit<ImageProps, "$hasError"> &
  Omit<React.ImgHTMLAttributes<HTMLImageElement>, "loading" | "width" | "height">;

export const BaseImage = forwardRef<HTMLImageElement, Props>(
  ({ disableLinkingData = false, lazyload = true, src, title, ...attribs }, forwardReference) => {
    const [hasError, setHasError] = useState<boolean | undefined>(undefined);

    // Workaround event before hydration can't be listen. Reference https://github.com/facebook/react/issues/15446
    const captureElement = useCallback(
      (element: HTMLImageElement | null) => {
        if (element?.complete) {
          setHasError(element.naturalWidth === 0);
        }

        if (forwardReference) {
          if (typeof forwardReference === "function") {
            forwardReference(element);
          } else {
            forwardReference.current = element;
          }
        }
      },
      [forwardReference],
    );

    return (
      <>
        <StyledImage
          {...attribs}
          $hasError={hasError}
          loading={lazyload ? "lazy" : "eager"}
          ref={captureElement}
          src={src}
          title={title}
          onError={() => {
            setHasError(true);
          }}
          onLoad={() => {
            setHasError(false);
          }}
        />
        {src && !disableLinkingData && (
          <JsonLd<ImageObject>
            item={{
              "@context": "https://schema.org",
              "@type": "ImageObject",
              caption: title,
              url: src,
            }}
          />
        )}
      </>
    );
  },
);

BaseImage.displayName = "BaseImage";
